/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {
    Box,
    Flex,
    Text,
    Button,
    useBreakpointValue,
    Container,
    Stack,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useUnavailableProductsInCartContext} from '../../../contexts/unavailable-products-in-cart-context'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {sendClickEvent} from '../../../utils/gtm-utils'

const StickyCheckoutBar = ({basket}) => {
    const styles = useMultiStyleConfig('Cart')
    const [isVisible, setIsVisible] = useState(false)
    const navigate = useNavigation()
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {UnavailableProductsInCart, unavailableProductIdsRef} =
        useUnavailableProductsInCartContext()

    useEffect(() => {
        const checkoutButton = document.querySelector('[data-testid="cart-checkout-button"]')
        const headerContainer = document.getElementById('app-header')

         if (!checkoutButton) {
            // Fallback: if button not found, hide sticky bar
            setIsVisible(false)
            return
        }

        if (isMobile) {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    // Show sticky bar when real button is NOT visible
                    setIsVisible(!entry.isIntersecting)
                },
                {
                    threshold: 0.1, // Trigger when at least 10% of button is visible
                    rootMargin: '0px'
                }
            )

            observer.observe(checkoutButton)

            return () => {
                observer.disconnect()
            }
        } else {
            // Desktop: Show bar when user scrolls past the viewport height (below the fold)
            const handleScroll = () => {
                const scrollPosition = window.scrollY
                const ctaBottomPositionFromTop =
                    // Distance from top
                    checkoutButton.getBoundingClientRect().top + scrollPosition +
                    // Button height to set the max to the CTA bottom
                    checkoutButton.getBoundingClientRect().height -
                    // Header height
                    headerContainer?.getBoundingClientRect().height || 0

                setIsVisible(scrollPosition > ctaBottomPositionFromTop)
            }

            window.addEventListener('scroll', handleScroll)
            handleScroll()

            return () => window.removeEventListener('scroll', handleScroll)
        }
    }, [isMobile])

    const handleCheckout = () => {
        if (unavailableProductIdsRef.current?.length > 0) {
            UnavailableProductsInCart.onOpen()
        } else {
            sendClickEvent('checkout_now_clicked')
            navigate('/checkout')
        }
    }

    const giftCardTotal = (basket?.paymentInstruments || [])
        .filter((instrument) => instrument.paymentMethodId === 'SMARTCLIXX')
        .reduce((sum, instrument) => sum + (instrument.amount || 0), 0)

    // Calculate estimated total - match order-summary calculation logic
    // When orderTotal is null (e.g., with certain promotions), explicitly add shipping and tax
    const estimatedShipping = Number(basket?.shippingTotal) || 0
    const estimatedTax = basket?.taxTotal != null ? Number(basket.taxTotal) : 0
    const estimatedTotal =
        basket?.orderTotal != null
            ? Number(basket.orderTotal)
            : (Number(basket?.productTotal) || 0) + estimatedShipping + estimatedTax
    const totalAmount = estimatedTotal - giftCardTotal
    const shouldShow = isVisible && !!basket

    return (
        <Box
            {...styles.stickyBarRoot}
            transform={shouldShow ? 'translateY(0)' : 'translateY(100%)'}
            visibility={shouldShow ? 'visible' : 'hidden'}
            pointerEvents={shouldShow ? 'auto' : 'none'}
            aria-hidden={!shouldShow}
        >
            <Container {...styles.stickyBarContainer}>
                {isMobile ? (
                    // Mobile Layout: Stacked
                    <Stack {...styles.stickyBarMobileStack}>
                        {/* Top Row: Estimated Total and Amount */}
                        <Flex {...styles.stickyBarRow}>
                            <Text {...styles.stickyBarLabel}>
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total"
                                />
                            </Text>
                            <Text {...styles.stickyBarAmount}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={totalAmount}
                                />
                            </Text>
                        </Flex>
                        {/* Bottom Row: Checkout Button */}
                        <Button
                            {...styles.stickyBarMobileButton}
                            variant="solid"
                            onClick={handleCheckout}
                            size="lg"
                        >
                            <FormattedMessage
                                id="cart_cta.checkout_button"
                                defaultMessage="Checkout"
                            />
                        </Button>
                    </Stack>
                ) : (
                    // Desktop Layout: Horizontal
                    <Flex {...styles.stickyBarDesktopRow}>
                        {/* Left: Estimated Total */}
                        <Flex {...styles.stickyBarDesktopTotal}>
                            <Text {...styles.stickyBarLabel}>
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total"
                                />
                                :
                            </Text>
                            <Text {...styles.stickyBarAmount}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={totalAmount}
                                />
                            </Text>
                        </Flex>
                        {/* Right: Checkout Button */}
                        <Button
                            {...styles.stickyBarDesktopButton}
                            variant="solid"
                            onClick={handleCheckout}
                            size="lg"
                        >
                            <FormattedMessage
                                id="cart_cta.checkout_button"
                                defaultMessage="Checkout"
                            />
                        </Button>
                    </Flex>
                )}
            </Container>
        </Box>
    )
}

StickyCheckoutBar.propTypes = {
    basket: PropTypes.object.isRequired
}

export default StickyCheckoutBar
